// ASCII 인쇄문자(0x21~0x7E)만 허용 → 공백, 비ASCII 전부 금지
const NON_ASCII_OR_SPACE = /[^\x21-\x7E]/;

// 특수문자 집합
const SPECIAL_CLASS = String.raw`!@#$%^&*()\-\_=+\[\]{};:'",.<>?/\\|~\``;
const SPECIAL = `[${SPECIAL_CLASS}]`;

// 규칙 A: (>=8자) & (대/소/숫/특 중 3종 이상)
const reA = new RegExp(
  String.raw`^(?!.*\s)(?!.*(.)\1{3,})(?=.{8,})` +
  String.raw`(?:(?=.*[A-Z])(?=.*[a-z])(?=.*\d)|(?=.*[A-Z])(?=.*[a-z])(?=.*${SPECIAL})|(?=.*[A-Z])(?=.*\d)(?=.*${SPECIAL})|(?=.*[a-z])(?=.*\d)(?=.*${SPECIAL}))` +
  String.raw`.*$`
);

// 규칙 B: (>=10자) & (대/소/특 중 2종 이상) — 숫자는 종류 카운트 제외(허용은 됨)
const reB = new RegExp(
  String.raw`^(?!.*\s)(?!.*(.)\1{3,})(?=.{10,})` +
  String.raw`(?:(?=.*[A-Z])(?=.*[a-z])|(?=.*[A-Z])(?=.*${SPECIAL})|(?=.*[a-z])(?=.*${SPECIAL}))` +
  String.raw`.*$`
);

function containsUserFragment(pw, { username, email } = {}) {
  const tokens = [];
  if (username) tokens.push(String(username));
  if (email) tokens.push(String(email).split('@')[0]);
  const lower = pw.toLowerCase();
  return tokens.some(t => {
    const s = String(t || '').toLowerCase();
    return s.length >= 3 && lower.includes(s);
  });
}

// 숫자/영문(동일 케이스)만 연속판정
function hasSequentialRun(pw, len = 4) {
  const isDigit = c => c >= '0' && c <= '9';
  const isLower = c => c >= 'a' && c <= 'z';
  const isUpper = c => c >= 'A' && c <= 'Z';

  let up = 1, down = 1;
  for (let i = 1; i < pw.length; i++) {
    const a = pw[i - 1], b = pw[i];

    const sameClass =
      (isDigit(a) && isDigit(b)) ||
      (isLower(a) && isLower(b)) ||
      (isUpper(a) && isUpper(b));

    if (!sameClass) { up = down = 1; continue; }

    const diff = b.charCodeAt(0) - a.charCodeAt(0);
    up   = (diff ===  1) ? up + 1 : 1;
    down = (diff === -1) ? down + 1 : 1;

    if (up >= len || down >= len) return true;
  }
  return false;
}

function hasKeyboardRowSequence(pw, min = 4) {
  // 키보드 연속시퀀스
  const rows = [
    "`1234567890-=",
    "qwertyuiop[]\\",
    "asdfghjkl;'",
    "zxcvbnm,./",
  ];
  const lower = pw.toLowerCase();

  for (const row of rows) {
    for (let i = 0; i <= row.length - min; i++) {
      const seq = row.slice(i, i + min);
      const rev = seq.split('').reverse().join('');
      if (lower.includes(seq) || lower.includes(rev)) return true;
    }
  }
  return false;
}

/**
 * 비밀번호 유효성 검사
 * @param {string} pw
 * @param {{username?: string, email?: string}} ctx
 * @returns {{ok: boolean, codes: string[], reasons: string[], value: string}}
 */
export function validatePassword(pw, ctx = {}) {
  const norm = String(pw || '').normalize('NFKC');

  const codes = []; // 실패 코드 목록(다국어 처리용 locale key)
  const reasons = []; // 실패 원인

  // 0) ASCII 전용 + 공백 금지
  if (NON_ASCII_OR_SPACE.test(norm)) {
    reasons.push('영문/숫자/특수문자(ASCII)만 사용할 수 있으며 공백은 금지됩니다.');
    codes.push('ascii-only');
  }

  // 1) 길이/구성 규칙 A or B
  if (!(reA.test(norm) || reB.test(norm))) {
    codes.push('rule');
    reasons.push('규칙 불충족: (8자 이상 & 대/소/숫/특 4종 중 3종) 또는 (10자 이상 & 대/소/특 3종 중 2종), 공백 금지, 동일문자 4회 연속 금지.');
  }

  // 2) 사용자 토큰 포함 금지
  if (containsUserFragment(norm, ctx)) {
    reasons.push('아이디/이메일 일부를 비밀번호에 포함할 수 없습니다.');
    codes.push('user-fragment');
  }

  // 3) 연속문자/키보드 시퀀스 금지
  if (hasSequentialRun(norm, 4)) {
    codes.push('sequential');
    reasons.push('연속된 알파벳/숫자를 4자 이상 사용할 수 없습니다.');
  }
  if (hasKeyboardRowSequence(norm, 4)) {
    reasons.push('연속된 키보드의 키를 4자 이상 사용할 수 없습니다.');
    codes.push('keyboard-row');
  }

  return { ok: codes.length === 0, reasons, codes, value: norm };
}

export const passwordRegex = { reA, reB };
export const constants = { SPECIAL_CLASS, SPECIAL };
